import { Check, Circle, X } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { pt } from "date-fns/locale";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { calendarKind } from "@/lib/notification-target";
import { categoryMeta, normalizeSeverity, SEVERITY_LABEL, SEVERITY_STYLE } from "@/lib/notification-meta";
import type { Notification } from "@/hooks/notifications/use-notifications";

interface Props {
  notification: Notification;
  selected: boolean;
  /** Há alguma selecionada: as caixas ficam sempre visíveis (não só ao passar o rato). */
  selecting: boolean;
  onOpen: (n: Notification) => void;
  onSelect: (id: string, next: boolean) => void;
  onToggleRead: (n: Notification) => void;
  onArchive: (n: Notification) => void;
  compact?: boolean;
}

/**
 * Uma notificação: barra de severidade à esquerda, ícone da categoria, título e texto, tempo e categoria.
 *
 * A cor é só da severidade (crítico, atenção); a categoria é um ícone neutro. Acções na própria linha,
 * visíveis ao passar o rato ou ao focar com o teclado — não é preciso abrir nada para marcar como lida.
 */
export function NotificationRow({ notification: n, selected, selecting, onOpen, onSelect, onToggleRead, onArchive, compact }: Props) {
  const severity = normalizeSeverity(n.severity);
  const style = SEVERITY_STYLE[severity];
  const category = categoryMeta(n.category);
  const Icon = category.icon;
  const resolved = !!n.resolved_at;
  // As de calendário distinguem convite/cancelamento no texto; o ícone fica o da categoria.
  const isCancelled = calendarKind(n) === "cancelled";

  return (
    <div
      className={cn(
        "group relative flex w-full items-start gap-3 px-4 text-left transition-colors hover:bg-foreground/3",
        compact ? "py-2.5" : "py-3",
        selected && "bg-foreground/5",
        resolved && "opacity-60",
      )}
    >
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-0.5", style.bar)} />

      <div className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
        <Checkbox
          checked={selected}
          onCheckedChange={(v) => onSelect(n.id, v === true)}
          aria-label={`Selecionar «${n.title}»`}
          className={cn("h-4 w-4", selecting || selected ? "opacity-100" : "opacity-0 focus-visible:opacity-100 group-hover:opacity-100")}
        />
        {!selecting && !selected && (
          <Icon
            aria-hidden
            strokeWidth={1.75}
            className={cn("pointer-events-none absolute inset-0 m-auto h-4 w-4 transition-opacity group-hover:opacity-0", style.icon, isCancelled && "text-destructive")}
          />
        )}
      </div>

      <button type="button" onClick={() => onOpen(n)} className="min-w-0 flex-1 text-left focus-visible:outline-hidden">
        <div className="flex items-baseline gap-2">
          <p className={cn("truncate text-[13px] leading-snug", n.is_read ? "text-muted-foreground" : "font-medium text-foreground")}>{n.title}</p>
          {!n.is_read && !resolved && <span aria-label="Por ler" className={cn("h-1.5 w-1.5 shrink-0 rounded-full", style.dot)} />}
        </div>
        <p className={cn("mt-0.5 text-xs leading-relaxed text-muted-foreground", compact ? "line-clamp-2" : "line-clamp-3")}>{n.message}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground/70">
          {severity !== "info" && <span className={cn("font-medium", style.icon)}>{SEVERITY_LABEL[severity]}</span>}
          {severity !== "info" && <span aria-hidden>·</span>}
          <span>{category.label}</span>
          <span aria-hidden>·</span>
          <time dateTime={n.created_at}>{formatDistanceToNowStrict(new Date(n.created_at), { addSuffix: true, locale: pt })}</time>
          {resolved && (
            <>
              <span aria-hidden>·</span>
              <span>resolvido</span>
            </>
          )}
        </p>
      </button>

      {!resolved && (
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <button
            type="button"
            onClick={() => onToggleRead(n)}
            title={n.is_read ? "Marcar como por ler" : "Marcar como lida"}
            aria-label={n.is_read ? "Marcar como por ler" : "Marcar como lida"}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/6 hover:text-foreground"
          >
            {n.is_read ? <Circle className="h-3.5 w-3.5" strokeWidth={1.75} /> : <Check className="h-3.5 w-3.5" strokeWidth={1.75} />}
          </button>
          <button
            type="button"
            onClick={() => onArchive(n)}
            title={n.notification_type === "alert" ? "Arquivar (não volta enquanto o problema durar)" : "Eliminar"}
            aria-label="Eliminar"
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        </div>
      )}
    </div>
  );
}
